import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJs,
  Tooltip as ChartTooltip,
  Legend,
  LinearScale,
  CategoryScale,
  LineElement,
  PointElement,
  Filler,
} from "chart.js";
import { FiBell, FiCalendar, FiChevronDown, FiShoppingCart, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import WalkInAlertsPill from "../../components/WalkInAlertsPill";
import { 
  IoDocumentsOutline, 
  IoPeopleOutline, 
  IoSpeedometerOutline, 
  IoCartOutline,
  IoWalkOutline,
  IoLogOutOutline
} from "react-icons/io5";

ChartJs.register(
  ChartTooltip,
  Legend,
  LinearScale,
  CategoryScale,
  LineElement,
  PointElement,
  Filler
);

interface OverviewData {
  filter: string;
  todayRevenue: number;
  totalOrders: number;
  totalCustomers: number;
  activeAgents: number;
  activeCarts: {
    active: number;
    total: number;
    display: string;
  };
}

interface CashFlowPoint {
  label: string;
  cash: number;
  bankTransfer: number;
  card: number;
  totalIncome: number;
}

interface CashFlowData {
  filter: string;
  period: { start: string; end: string };
  totals: { cash: number; bankTransfer: number; card: number; totalIncome: number };
  points: CashFlowPoint[];
}

interface PaymentBreakdownMethod {
  amount: number;
  percentage: number;
}

interface PaymentBreakdownData {
  filter: string;
  date: string;
  totalIncome: number;
  methods: {
    cash: PaymentBreakdownMethod;
    bankTransfer: PaymentBreakdownMethod;
    card: PaymentBreakdownMethod;
  };
}

type CashFlowFilter = "today" | "last_week";
type BreakdownFilter = "today" | "this_week" | "last_week" | "month" | "yearly";

const BREAKDOWN_FILTER_LABELS: { value: BreakdownFilter; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "month", label: "This Month" },
  { value: "yearly", label: "This Year" },
];

interface Activity {
  type: string;
  action: string;
  agentName: string;
  agentId: string;
  orderId: string;
  orderNumber: string;
  amount: number;
  customerName: string | null;
  createdAt: string;
}

interface LiveCartItem {
  id: string;
  cartId: string;
  customerName: string;
  status: string;
  items: { name: string; image: string; measurement: string; type: string; quantity: number; totalPrice: number; pricePerQuantity: number; productId: string; _id: string }[];
  itemCount: number;
  value: number;
  lastActive: string;
  agent: string;
  agentId: string;
  updatedAt: string;
  createdAt: string;
  activityDate: string;
}

interface CartDetailsData {
  header: { cartId: string; customerName: string; status: string };
  customer: { name: string; email: string | null; phoneNumber: string | null };
  cartSummary: { items: number; amount: number; paymentMethod: string; lastActive: string };
  agentOnCart: { id: string; name: string; role: string };
  products: { name: string; image: string | null; price: number; quantity: number; subTotal: number }[];
  cartTimeline: { event: string; title: string; occurredAt: string; meta: string }[];
}

const ACTIVITIES_LIMIT = 5;
const LIVE_CARTS_LIMIT = 5;

export default function WalkInDashboard() {
  const [activeCashFlowTab, setActiveCashFlowTab] = useState<"Cash" | "Card" | "Transfer" | "Total">("Cash");
  const [timeRange, setTimeRange] = useState<"Today" | "Last week">("Today");
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cashFlow, setCashFlow] = useState<CashFlowData | null>(null);
  const [cashFlowLoading, setCashFlowLoading] = useState(true);

  const [breakdown, setBreakdown] = useState<PaymentBreakdownData | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(true);
  const [breakdownFilter, setBreakdownFilter] = useState<BreakdownFilter>("today");

  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesCount, setActivitiesCount] = useState(0);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  const [liveCarts, setLiveCarts] = useState<LiveCartItem[]>([]);
  const [liveCartsTotal, setLiveCartsTotal] = useState(0);
  const [liveCartsPage, setLiveCartsPage] = useState(1);
  const [liveCartsLoading, setLiveCartsLoading] = useState(true);

  // Cart details modal state
  const [selectedCartId, setSelectedCartId] = useState<string | null>(null);
  const [selectedCartDetails, setSelectedCartDetails] = useState<CartDetailsData | null>(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  /** Shared token helper */
  const getToken = () => {
    const auth = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    return jsonData?.token as string | undefined;
  };

  const authHeaders = (token?: string) =>
    token ? { Authorization: `Bearer ${token}` } : undefined;

  const fetchOverview = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/overview`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setOverview(res.data.data);
      } else {
        setError("Unexpected response from server");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to fetch overview";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchCashFlow = useCallback(async (filter: CashFlowFilter) => {
    setCashFlowLoading(true);
    try {
      const token = getToken();
      const query = filter !== "today" ? `?filter=${filter}` : "";
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/cash-flow${query}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) setCashFlow(res.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch cash flow");
    } finally {
      setCashFlowLoading(false);
    }
  }, []);

  const fetchBreakdown = useCallback(async (filter: BreakdownFilter) => {
    setBreakdownLoading(true);
    try {
      const token = getToken();
      const query = filter !== "today" ? `?filter=${filter}` : "";
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/payment-breakdown${query}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) setBreakdown(res.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch payment breakdown");
    } finally {
      setBreakdownLoading(false);
    }
  }, []);

  const fetchActivities = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/activities/realtime?limit=100`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setActivities(res.data.data.activities ?? []);
        setActivitiesCount(res.data.data.count ?? 0);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch activities");
    } finally {
      setActivitiesLoading(false);
    }
  }, []);

  const fetchLiveCarts = useCallback(async () => {
    setLiveCartsLoading(true);
    try {
      const token = getToken();
      const queryParams = new URLSearchParams();
      queryParams.append("filter", "today");
      queryParams.append("status", "all");
      queryParams.append("limit", "100000");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/live-carts-summary?${queryParams.toString()}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setLiveCarts(res.data.data.data ?? []);
        setLiveCartsTotal(res.data.data.data?.length ?? 0);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch live carts");
    } finally {
      setLiveCartsLoading(false);
    }
  }, []);

  const fetchCartDetails = useCallback(async (cartId: string) => {
    setIsDetailsLoading(true);
    setDetailsError(null);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/live-carts/${cartId}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setSelectedCartDetails(res.data.data);
      } else {
        setDetailsError("Unexpected response format from server");
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || "Failed to fetch cart details";
      setDetailsError(msg);
      toast.error(msg);
    } finally {
      setIsDetailsLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  useEffect(() => {
    const filter: CashFlowFilter = timeRange === "Last week" ? "last_week" : "today";
    fetchCashFlow(filter);
  }, [timeRange, fetchCashFlow]);

  useEffect(() => {
    fetchBreakdown(breakdownFilter);
  }, [breakdownFilter, fetchBreakdown]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  useEffect(() => {
    fetchLiveCarts();
  }, [fetchLiveCarts]);

  // Fetch details when a cart is selected
  useEffect(() => {
    if (selectedCartId) {
      fetchCartDetails(selectedCartId);
    } else {
      setSelectedCartDetails(null);
      setDetailsError(null);
    }
  }, [selectedCartId, fetchCartDetails]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 2 }).format(amount);

  const timeAgo = (dateStr: string) => {
    if (!dateStr) return "Just now";
    const timestamp = new Date(dateStr).getTime();
    if (isNaN(timestamp)) return "Just now";
    const diff = Date.now() - timestamp;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const actionLabel = (action: string) => {
    switch (action) {
      case "completed_order": return "completed order";
      case "started_order": return "started order";
      case "cancelled_order": return "cancelled order";
      default: return action.replace(/_/g, " ");
    }
  };

  const formatStatus = (s: string) => {
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const activitiesTotalPages = Math.max(1, Math.ceil(activities.length / ACTIVITIES_LIMIT));
  const liveCartsTotalPages = Math.max(1, Math.ceil(liveCarts.length / LIVE_CARTS_LIMIT));

  const paginatedActivities = activities.slice((activitiesPage - 1) * ACTIVITIES_LIMIT, activitiesPage * ACTIVITIES_LIMIT);
  const paginatedLiveCarts = liveCarts.slice((liveCartsPage - 1) * LIVE_CARTS_LIMIT, liveCartsPage * LIVE_CARTS_LIMIT);
  // Derive chart data from API points based on active tab
  const chartLabels = cashFlow?.points?.map((p) => p.label) ?? [];
  const chartValues = cashFlow?.points?.map((p) => {
    switch (activeCashFlowTab) {
      case "Cash": return p.cash;
      case "Card": return p.card;
      case "Transfer": return p.bankTransfer;
      case "Total": return p.totalIncome;
    }
  }) ?? [];
  const chartMax = chartValues.length > 0 ? Math.max(...chartValues) * 1.3 : 50000;

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        data: chartValues,
        borderColor: "#008060",
        backgroundColor: (context: any) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return "transparent";
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, "rgba(0, 128, 96, 0.25)");
          gradient.addColorStop(1, "rgba(0, 128, 96, 0.0)");
          return gradient;
        },
        fill: true,
        tension: 0.45,
        borderWidth: 2,
        pointBackgroundColor: "#008060",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      }
    ]
  };

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "#E5F6F0",
        titleColor: "#008060",
        bodyColor: "#008060",
        borderColor: "#c1ebd6",
        borderWidth: 1,
        padding: 8,
        displayColors: false,
        callbacks: {
          label: (ctx: any) => formatCurrency(ctx.parsed.y),
        },
      },
    },
    scales: {
      x: { 
        grid: { display: false }, 
        ticks: { color: "#9CA3AF", font: { size: 11 } } 
      },
      y: {
        grid: { color: "#f3f4f6" },
        min: 0,
        max: Math.ceil(chartMax / 10000) * 10000 || 50000,
        ticks: {
          color: "#9CA3AF",
          font: { size: 11 },
          callback: (v: number) => v >= 1000 ? `${v / 1000}k` : v,
        },
      },
    },
  };

  const customPlugins: any[] = [];

  return (
    <div className="w-full min-h-screen p-4 xl:p-6 flex flex-col gap-6 font-sans">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-gray-950">Overview</h1>
        </div>

        {/* Top-right Controls */}
        <div className="flex items-center gap-3 self-stretch md:self-auto flex-wrap">
          {/* Alerts Pill */}
          <WalkInAlertsPill />

          {/* Date Picker Pill */}
          <button className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-100 shadow-sm text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors">
            <FiCalendar className="w-4 h-4 text-[#008060]" />
            <span>{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}, {new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span>
          </button>

          {/* Export Pill */}
          <button className="flex items-center justify-between gap-3 px-4 py-2 bg-[#008060] text-white rounded-lg text-sm font-semibold hover:bg-[#006b50] transition-colors shadow-sm min-w-[150px]">
            <span>Export Summary</span>
            <FiChevronDown className="text-white" />
          </button>
        </div>
      </div>

      {/* Row of Metrics (5 cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
        {/* Card 1: Today's Revenue (Dark gradient) */}
        <div className="bg-gradient-to-br from-[#0c2219] to-[#040e0a] p-5 rounded-2xl shadow-sm border border-[#173a2b] flex items-center justify-between min-h-[120px] transition-all hover:scale-[1.01]">
          <div>
            <p className="text-gray-400 text-xs font-medium tracking-wide">Today's Revenue</p>
            <h3 className="text-[24px] font-bold text-white mt-1.5">
              {isLoading ? <span className="inline-block w-24 h-7 bg-gray-700 rounded animate-pulse" /> : formatCurrency(overview?.todayRevenue ?? 0)}
            </h3>

          </div>
          <div className="flex items-end gap-1 h-10 pr-1">
            <div className="w-[3.5px] h-4 bg-[#008060] rounded-sm"></div>
            <div className="w-[3.5px] h-6 bg-[#008060] rounded-sm"></div>
            <div className="w-[3.5px] h-8 bg-[#008060] rounded-sm"></div>
            <div className="w-[3.5px] h-5 bg-[#4ade80] rounded-sm"></div>
          </div>
        </div>

        {/* Card 2: Total Number of Orders Today */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex items-center justify-between min-h-[120px] transition-all hover:scale-[1.01]">
          <div>
            <p className="text-gray-400 text-xs font-medium tracking-wide">Total Number of Orders Today</p>
            <h3 className="text-[24px] font-bold text-gray-900 mt-1.5">
              {isLoading ? <span className="inline-block w-12 h-7 bg-gray-100 rounded animate-pulse" /> : (overview?.totalOrders ?? 0)}
            </h3>

          </div>
          <div className="p-3 bg-[#E6F5EE] rounded-xl text-[#008060]">
            <IoDocumentsOutline className="w-5 h-5" />
          </div>
        </div>

        {/* Card 3: Total Number of Customers */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex items-center justify-between min-h-[120px] transition-all hover:scale-[1.01]">
          <div>
            <p className="text-gray-400 text-xs font-medium tracking-wide">Total Number of Customers</p>
            <h3 className="text-[24px] font-bold text-gray-900 mt-1.5">
              {isLoading ? <span className="inline-block w-12 h-7 bg-gray-100 rounded animate-pulse" /> : (overview?.totalCustomers ?? 0)}
            </h3>

          </div>
          <div className="p-3 bg-[#EAF2FD] rounded-xl text-blue-500">
            <IoPeopleOutline className="w-5 h-5" />
          </div>
        </div>

        {/* Card 4: Active Front Desk Agent */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex items-center justify-between min-h-[120px] transition-all hover:scale-[1.01]">
          <div>
            <p className="text-gray-400 text-xs font-medium tracking-wide">Active Front Desk Agent</p>
            <h3 className="text-[24px] font-bold text-gray-900 mt-1.5">
              {isLoading ? <span className="inline-block w-12 h-7 bg-gray-100 rounded animate-pulse" /> : (overview?.activeAgents ?? 0)}
            </h3>

          </div>
          <div className="p-3 bg-[#FFF6E9] rounded-xl text-amber-500">
            <IoSpeedometerOutline className="w-5 h-5" />
          </div>
        </div>

        {/* Card 5: Active Cart */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex items-center justify-between min-h-[120px] transition-all hover:scale-[1.01]">
          <div>
            <p className="text-gray-400 text-xs font-medium tracking-wide">Active Cart</p>
            <h3 className="text-[24px] font-bold text-gray-900 mt-1.5">
              {isLoading ? <span className="inline-block w-12 h-7 bg-gray-100 rounded animate-pulse" /> : (overview?.activeCarts?.display ?? "0/0")}
            </h3>

          </div>
          <div className="p-3 bg-[#EAF9F2] rounded-xl text-[#008060]">
            <IoCartOutline className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Middle Grid - Cash Flow (2/3) vs Payment Breakdown (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Today's Cash Flow Area Graph */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-50 p-5 flex flex-col justify-between relative min-h-[380px]">
          {/* Header */}
          <div className="flex justify-between items-center pb-2">
            <h2 className="text-base font-bold text-gray-800">
              {timeRange === "Today"
                ? `Today's Cash flow (${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short" })})`
                : "Last Week's Cash flow"}
            </h2>
            <div className="flex items-center gap-4">
              <div className="flex bg-gray-50 p-0.5 rounded-lg border border-gray-100">
                <button
                  onClick={() => setTimeRange("Today")}
                  className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                    timeRange === "Today"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-400 hover:text-gray-700"
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setTimeRange("Last week")}
                  className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${
                    timeRange === "Last week"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-400 hover:text-gray-700"
                  }`}
                >
                  Last week
                </button>
              </div>
              <button className="text-gray-400 hover:text-gray-600 font-bold text-lg leading-none">⋮</button>
            </div>
          </div>

          {/* Cash flow Tab Header Row */}
          <div className="grid grid-cols-4 border-b border-gray-100 pb-3 mt-1.5">
            <button
              onClick={() => setActiveCashFlowTab("Cash")}
              className={`flex flex-col text-left pb-2 transition-all border-b-2 ${
                activeCashFlowTab === "Cash" ? "border-[#008060]" : "border-transparent"
              }`}
            >
              <span className={`text-[11px] font-semibold ${activeCashFlowTab === "Cash" ? "text-[#008060]" : "text-gray-400"}`}>Cash</span>
              <span className="text-base font-bold text-gray-900 mt-1">
                {cashFlowLoading ? <span className="inline-block w-20 h-5 bg-gray-100 rounded animate-pulse" /> : formatCurrency(cashFlow?.totals?.cash ?? 0)}
              </span>
            </button>

            <button
              onClick={() => setActiveCashFlowTab("Card")}
              className={`flex flex-col text-left pb-2 transition-all border-b-2 ${
                activeCashFlowTab === "Card" ? "border-[#008060]" : "border-transparent"
              }`}
            >
              <span className={`text-[11px] font-semibold ${activeCashFlowTab === "Card" ? "text-[#008060]" : "text-gray-400"}`}>Card</span>
              <span className="text-base font-bold text-gray-900 mt-1">
                {cashFlowLoading ? <span className="inline-block w-20 h-5 bg-gray-100 rounded animate-pulse" /> : formatCurrency(cashFlow?.totals?.card ?? 0)}
              </span>
            </button>

            <button
              onClick={() => setActiveCashFlowTab("Transfer")}
              className={`flex flex-col text-left pb-2 transition-all border-b-2 ${
                activeCashFlowTab === "Transfer" ? "border-[#008060]" : "border-transparent"
              }`}
            >
              <span className={`text-[11px] font-semibold ${activeCashFlowTab === "Transfer" ? "text-[#008060]" : "text-gray-400"}`}>Transfer</span>
              <span className="text-base font-bold text-gray-900 mt-1">
                {cashFlowLoading ? <span className="inline-block w-20 h-5 bg-gray-100 rounded animate-pulse" /> : formatCurrency(cashFlow?.totals?.bankTransfer ?? 0)}
              </span>
            </button>

            <button
              onClick={() => setActiveCashFlowTab("Total")}
              className={`flex flex-col text-left pb-2 transition-all border-b-2 ${
                activeCashFlowTab === "Total" ? "border-[#008060]" : "border-transparent"
              }`}
            >
              <span className={`text-[11px] font-semibold ${activeCashFlowTab === "Total" ? "text-[#008060]" : "text-gray-400"}`}>Total income</span>
              <span className="text-base font-bold text-gray-900 mt-1">
                {cashFlowLoading ? <span className="inline-block w-20 h-5 bg-gray-100 rounded animate-pulse" /> : formatCurrency(cashFlow?.totals?.totalIncome ?? 0)}
              </span>
            </button>
          </div>

          {/* Chart */}
          <div className="relative h-[200px] w-full mt-4">
            {cashFlowLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]" />
              </div>
            ) : (
              <Line data={chartData} options={chartOptions} plugins={customPlugins} />
            )}
          </div>
        </div>

        {/* Right: Payment Method Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-50 p-5 flex flex-col justify-between min-h-[380px]">
          <div>
            <div className="flex justify-between items-center pb-4">
              <h2 className="text-base font-bold text-gray-800">Payment Method Breakdown</h2>
              <div className="relative">
                <select
                  value={breakdownFilter}
                  onChange={(e) => setBreakdownFilter(e.target.value as BreakdownFilter)}
                  className="appearance-none bg-gray-50 border border-gray-100 rounded-lg pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:border-[#008060] cursor-pointer"
                >
                  {BREAKDOWN_FILTER_LABELS.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            {breakdownLoading ? (
              <div className="flex flex-col gap-6 mt-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <div className="w-full h-3 bg-gray-100 rounded animate-pulse" />
                    <div className="w-full h-2 bg-gray-50 rounded-full" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-6 mt-4">
                {/* Cash */}
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center text-xs font-semibold text-gray-700">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#008060]" />Cash
                    </span>
                    <span className="text-gray-500">{breakdown?.methods?.cash?.percentage ?? 0}% · {formatCurrency(breakdown?.methods?.cash?.amount ?? 0)}</span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-[#008060] h-full rounded-full transition-all duration-500" style={{ width: `${breakdown?.methods?.cash?.percentage ?? 0}%` }} />
                  </div>
                </div>

                {/* Card */}
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center text-xs font-semibold text-gray-700">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500" />Card
                    </span>
                    <span className="text-gray-500">{breakdown?.methods?.card?.percentage ?? 0}% · {formatCurrency(breakdown?.methods?.card?.amount ?? 0)}</span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full transition-all duration-500" style={{ width: `${breakdown?.methods?.card?.percentage ?? 0}%` }} />
                  </div>
                </div>

                {/* Transfer */}
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center text-xs font-semibold text-gray-700">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />Transfer
                    </span>
                    <span className="text-gray-500">{breakdown?.methods?.bankTransfer?.percentage ?? 0}% · {formatCurrency(breakdown?.methods?.bankTransfer?.amount ?? 0)}</span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-amber-500 h-full rounded-full transition-all duration-500" style={{ width: `${breakdown?.methods?.bankTransfer?.percentage ?? 0}%` }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Row Totals */}
          <div className="flex justify-between items-center border-t border-gray-100 pt-4 mt-6">
            <span className="text-sm font-bold text-gray-800 tracking-wider">TOTAL</span>
            <span className="text-[17px] font-black text-gray-950">
              {breakdownLoading ? <span className="inline-block w-24 h-5 bg-gray-100 rounded animate-pulse" /> : formatCurrency(breakdown?.totalIncome ?? 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom Grid - Real-time Activity vs Live Carts Table */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Real-time Activity (5/12 width) */}
        <div className="lg:col-span-5 bg-white rounded-xl shadow-sm border border-gray-50 p-5 flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-800">Real-time Activity</h2>
                <span className="px-2 py-0.5 bg-[#EAF9F2] text-[#008060] rounded text-[10px] font-semibold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Live
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Latest platform activities</p>
            </div>
            <button className="text-gray-300 hover:text-gray-500">⋮</button>
          </div>

          {/* List items */}
          <div className="flex flex-col gap-4 mt-2 min-h-[300px]">
            {activitiesLoading ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]" />
              </div>
            ) : activities.length === 0 ? (
              <div className="flex justify-center items-center h-full text-gray-400 text-sm">
                No recent activities.
              </div>
            ) : (
              paginatedActivities.map((activity, idx) => (
                <div key={`${activity.orderId}-${idx}`} className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0">
                  {activity.action === "completed_order" ? (
                    <div className="p-2 bg-[#E6F5EE] rounded-lg text-[#008060] mt-0.5 shrink-0">
                      <IoCartOutline className="w-4 h-4" />
                    </div>
                  ) : activity.action === "started_order" ? (
                    <div className="p-2 bg-[#FFF6E9] rounded-lg text-amber-500 mt-0.5 shrink-0">
                      <IoWalkOutline className="w-4 h-4" />
                    </div>
                  ) : (
                    <div className="p-2 bg-[#EAF2FD] rounded-lg text-blue-500 mt-0.5 shrink-0">
                      <IoLogOutOutline className="w-4 h-4" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 leading-normal truncate">
                      <span className="font-bold text-gray-900">{activity.agentName || "Unknown"}</span> {actionLabel(activity.action)} {activity.orderNumber ? `#${activity.orderNumber}` : ""}
                    </p>
                    <span className="text-xs text-gray-400 mt-1 block">{timeAgo(activity.createdAt)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Pagination */}
          {!activitiesLoading && (
            <div className="flex justify-between items-center mt-2 border-t border-gray-50 pt-3">
              <button 
                onClick={() => setActivitiesPage(p => Math.max(1, p - 1))}
                disabled={activitiesPage === 1}
                className="text-xs font-semibold text-gray-600 disabled:text-gray-300 transition-colors"
              >
                Previous
              </button>
              <span className="text-xs text-gray-400">Page {activitiesPage} of {activitiesTotalPages}</span>
              <button 
                onClick={() => setActivitiesPage(p => Math.min(activitiesTotalPages, p + 1))}
                disabled={activitiesPage >= activitiesTotalPages}
                className="text-xs font-semibold text-[#008060] disabled:text-gray-300 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Right: Live Cart (7/12 width) */}
        <div className="lg:col-span-7 bg-white rounded-xl shadow-sm border border-gray-50 p-5 flex flex-col gap-4">
          <div className="pb-1">
            <h2 className="text-base font-bold text-gray-800">Live Cart (Ongoing Transactions)</h2>
          </div>

          <div className="overflow-x-auto min-h-[300px] flex flex-col">
            {liveCartsLoading ? (
              <div className="flex justify-center items-center flex-1">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]" />
              </div>
            ) : liveCarts.length === 0 ? (
              <div className="flex justify-center items-center flex-1 text-gray-400 text-sm">
                No active carts.
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-100 text-xs font-semibold">
                    <th className="pb-3 font-semibold">Cart / Customer</th>
                    <th className="pb-3 font-semibold">Status</th>
                    <th className="pb-3 font-semibold text-center">Items</th>
                    <th className="pb-3 font-semibold">Agent</th>
                    <th className="pb-3 text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 text-gray-700 font-medium">
                  {paginatedLiveCarts.map((cart) => {
                    const statusStr = cart.status?.toLowerCase() || "";
                    const isActive = statusStr === "active" || statusStr === "processing";
                    const isDraft = statusStr === "draft";
                    return (
                      <tr key={cart.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3">
                          <p className="text-gray-900 font-bold text-xs">{cart.cartId}</p>
                          <p className="text-gray-400 text-[11px] mt-0.5">{cart.customerName}</p>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : isDraft ? "bg-amber-500" : "bg-red-500"}`} />
                            <span className={`text-[11px] font-bold ${isActive ? "text-emerald-500" : isDraft ? "text-amber-500" : "text-red-500"}`}>
                              {formatStatus(cart.status)}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 text-center text-gray-500 text-xs">{cart.itemCount}</td>
                        <td className="py-3">
                          <span className="text-[#008060] font-bold text-xs">{cart.agent}</span>
                        </td>
                        <td className="py-3 text-right">
                          <button
                            onClick={() => setSelectedCartId(cart.id)}
                            className="text-[11px] text-blue-600 hover:text-blue-800 transition-colors font-bold"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Pagination */}
          {!liveCartsLoading && liveCarts.length > 0 && (
            <div className="flex justify-between items-center mt-auto border-t border-gray-50 pt-3">
              <span className="text-[11px] text-gray-400 font-medium">
                Page {liveCartsPage} of {liveCartsTotalPages}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setLiveCartsPage(p => Math.max(1, p - 1))}
                  disabled={liveCartsPage === 1}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <FiChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setLiveCartsPage(p => Math.min(liveCartsTotalPages, p + 1))}
                  disabled={liveCartsPage >= liveCartsTotalPages}
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <FiChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cart Details Modal */}
      {selectedCartId && (
        <div className="fixed inset-0 bg-black/45 flex items-center justify-center p-4 z-50 overflow-y-auto" style={{ animation: "fadeIn 0.15s ease" }}>
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto border border-gray-100/50 p-6 flex flex-col gap-6 relative">
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-gray-50 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gray-50 rounded-xl text-gray-600">
                  <FiShoppingCart className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    {selectedCartDetails?.header?.cartId || "Loading..."}
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {selectedCartDetails?.header?.customerName || "..."}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedCartDetails?.header?.status && (
                  <span className="px-3.5 py-1 bg-[#EAF9F2] text-[#008060] text-xs font-bold rounded-full">
                    {formatStatus(selectedCartDetails.header.status)}
                  </span>
                )}
                <button
                  onClick={() => { setSelectedCartId(null); setSelectedCartDetails(null); setDetailsError(null); }}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-50 transition-colors ml-2"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {isDetailsLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-10 h-10 border-4 border-[#EAF5EF] border-t-[#008060] rounded-full animate-spin" />
                <p className="text-xs text-gray-400 font-semibold tracking-wide animate-pulse">Retrieving cart details...</p>
              </div>
            ) : detailsError ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-gray-200 rounded-2xl bg-gray-50/50">
                <p className="text-sm text-gray-500 font-medium mb-3">{detailsError}</p>
                <button onClick={() => fetchCartDetails(selectedCartId)} className="px-4 py-2 bg-[#008060] text-white rounded-lg text-xs font-bold hover:bg-[#006b50] transition-colors shadow-sm">
                  Retry Load
                </button>
              </div>
            ) : selectedCartDetails ? (
              <>
                {/* Customer Details */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Customer</h4>
                  <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/15 flex flex-col gap-2.5">
                    <p className="text-sm text-gray-500 font-medium">Name: <span className="text-gray-800 font-semibold ml-1">{selectedCartDetails.customer.name || "Walk-in Customer"}</span></p>

                  </div>
                </div>

                {/* Cart Summary */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cart Summary</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50/30 border border-gray-100 p-3 rounded-2xl text-center flex flex-col justify-center items-center min-h-[75px]">
                      <span className="text-lg font-black text-gray-900">{selectedCartDetails.cartSummary.items}</span>
                      <span className="text-[10px] font-bold text-gray-400 tracking-wide uppercase mt-1">Items</span>
                    </div>
                    <div className="bg-gray-50/30 border border-gray-100 p-3 rounded-2xl text-center flex flex-col justify-center items-center min-h-[75px]">
                      <span className="text-lg font-black text-[#008060]">{formatCurrency(selectedCartDetails.cartSummary.amount)}</span>
                      <span className="text-[10px] font-bold text-gray-400 tracking-wide uppercase mt-1">Amount</span>
                    </div>
                    <div className="bg-gray-50/30 border border-gray-100 p-3 rounded-2xl text-center flex flex-col justify-center items-center min-h-[75px]">
                      <span className="text-sm font-bold text-gray-900">{formatStatus(selectedCartDetails.cartSummary.paymentMethod)}</span>
                      <span className="text-[10px] font-bold text-[#008060] tracking-wide uppercase mt-1">Payment</span>
                    </div>
                  </div>
                </div>

                {/* Agent */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Agent on Cart</h4>
                  <div className="border border-gray-100 rounded-2xl p-4 bg-gray-50/15 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#EAF9F2] text-[#008060] font-black text-sm flex items-center justify-center select-none shadow-sm">
                      {selectedCartDetails.agentOnCart.name ? selectedCartDetails.agentOnCart.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "UN"}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{selectedCartDetails.agentOnCart.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{selectedCartDetails.agentOnCart.role}</p>
                    </div>
                  </div>
                </div>

                {/* Products */}
                <div className="flex flex-col gap-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Products</h4>
                  <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-gray-400 text-[10px] font-bold">
                          <th className="py-2.5 px-4 font-bold tracking-wide">PRODUCTS</th>
                          <th className="py-2.5 px-3 font-bold tracking-wide">PRICE</th>
                          <th className="py-2.5 px-3 text-center font-bold tracking-wide">QTY</th>
                          <th className="py-2.5 px-4 text-right font-bold tracking-wide">SUB-TOTAL</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 text-gray-700 font-medium">
                        {selectedCartDetails.products && selectedCartDetails.products.length > 0 ? (
                          selectedCartDetails.products.map((prod, pIdx) => (
                            <tr key={pIdx} className="hover:bg-gray-50/20 transition-colors">
                              <td className="py-3 px-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100/50 flex items-center justify-center overflow-hidden shrink-0">
                                  {prod.image ? (
                                    <img src={prod.image} alt={prod.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                                  ) : (
                                    <FiShoppingCart className="w-5 h-5 text-orange-400" />
                                  )}
                                </div>
                                <p className="font-bold text-gray-900">{prod.name}</p>
                              </td>
                              <td className="py-3 px-3 text-gray-500">{formatCurrency(prod.price)}</td>
                              <td className="py-3 px-3 text-center text-gray-900 font-bold">{prod.quantity}</td>
                              <td className="py-3 px-4 text-right text-gray-900 font-bold">{formatCurrency(prod.subTotal)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={4} className="text-center py-4 text-gray-400">No items in this basket.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Timeline */}
                {selectedCartDetails.cartTimeline && selectedCartDetails.cartTimeline.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cart Timeline</h4>
                    <div className="border border-gray-100 rounded-2xl p-5 bg-gray-50/15 flex flex-col gap-5 relative">
                      {selectedCartDetails.cartTimeline.map((step, sIdx, arr) => {
                        const isLast = sIdx === arr.length - 1;
                        return (
                          <div key={sIdx} className="flex gap-4 relative">
                            {!isLast && <span className="absolute left-2.5 top-6 bottom-0 w-0.5 bg-emerald-500" />}
                            <span className="w-5 h-5 rounded-full flex items-center justify-center z-10 border-4 bg-white border-emerald-500">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            </span>
                            <div className="flex flex-col gap-0.5">
                              <p className="text-xs font-bold text-gray-900">{step.title}</p>
                              <p className="text-[10px] text-gray-400">
                                {new Date(step.occurredAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                                &nbsp;&middot;&nbsp; {step.meta}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
